/**
 * Anonymous product analytics.
 *
 * ---------------------------------------------------------------------------
 * THE RULE
 * ---------------------------------------------------------------------------
 * Event names and small non-identifying counters only. Never the content of an
 * answer, never free text, never anything that could identify a person.
 *
 * The `AnalyticsEvent` union below is exhaustive and is the complete list of
 * everything this app is capable of recording. If an event is not in that
 * union it cannot be sent, which makes the privacy claim on the site checkable
 * rather than aspirational. The server enforces the same list a second time —
 * see `Validation/AnalyticsEventValidator.cs` — because this half of the
 * promise runs on a machine the visitor controls.
 *
 * ---------------------------------------------------------------------------
 * WHAT LINKS TO WHAT
 * ---------------------------------------------------------------------------
 * There is no cookie, no localStorage identifier and no device fingerprint.
 * There is exactly one identifier, `tabId`: a random value that lives in
 * sessionStorage and is gone when the tab closes. It exists so a funnel can say
 * "of twenty who started, twelve reached question five" instead of counting raw
 * hits, which is the difference between knowing where people stop and knowing
 * nothing useful at all.
 *
 * It cannot follow anyone to another tab, another device, or tomorrow.
 *
 * The server separately derives a daily visitor code from the connection, which
 * is what makes "how many people" answerable; that never touches this device.
 * Both are described in plain words on /privacy, and that page and this comment
 * have to keep saying the same thing.
 *
 * Off by default: set VITE_ANALYTICS_PROVIDER to turn it on.
 */

export type AnalyticsEvent =
  | { name: 'page_opened'; page: string; source?: string }
  | { name: 'journey_started'; profileId: string }
  | { name: 'journey_resumed'; profileId: string }
  | { name: 'question_reached'; profileId: string; questionIndex: number }
  /** A round is finished. `roundIndex` is 0-based; the count, not the content. */
  | { name: 'journey_round_completed'; profileId: string; roundIndex: number }
  /** Every question in the set is answered. */
  | { name: 'journey_completed'; profileId: string }
  | { name: 'journey_restarted'; profileId: string }
  /**
   * Chose to carry on past the result rather than stop at the round they had.
   *
   * `roundIndex` is the round being entered, NOT the one just finished — so
   * carrying on after round one is 1, where `journey_round_completed` calls
   * that same moment 0. The two are one apart on purpose and it is a trap when
   * counting; see the note above query 4 in the README.
   */
  | { name: 'journey_continued'; profileId: string; roundIndex: number }
  | {
      name: 'result_shown';
      profileId: string;
      category: string;
      /**
       * Rounds finished at the moment the result was shown. Carried so that
       * "what share of the people who were offered a second round took it" has
       * a denominator — without it, `journey_continued` is a numerator over
       * nothing, because `result_shown` fires for every round alike.
       */
      roundsDone: number;
    }
  | {
      name: 'contact_form_opened';
      profileId: string;
      /** The result she is writing from, or "none" on the direct route. */
      category: string;
      /**
       * How she got to the form: the button in the result page, the sticky bar
       * standing in for it, the quiet link on the profile that skips the
       * questions, or `/:slug/contact` typed or scanned directly.
       */
      source: 'inline' | 'sticky' | 'profile' | 'direct';
    }
  | {
      name: 'contact_form_submitted';
      profileId: string;
      category: string;
      /** Whether answers were shared. A boolean, not the answers. */
      sharedAnswers: boolean;
    }
  | { name: 'contact_form_failed'; profileId: string };

type Provider = 'none' | 'console' | 'beacon';

const provider: Provider = (import.meta.env.VITE_ANALYTICS_PROVIDER ?? 'none') as Provider;

/**
 * Same-origin by default. The API serves the built SPA in production, so this
 * is a first-party request to the same host — which matters more than it
 * sounds: the traffic here arrives by QR code from a card, and a third-party
 * analytics domain would have a meaningful slice of it dropped by content
 * blockers before it ever left the browser.
 */
// `||`, not `??`. An unset variable arrives as undefined, but `.env.example`
// ships the key with an empty value and a copied file therefore yields ''.
// Nullish coalescing would take that empty string as a deliberate choice, and
// the guard further down would then quietly send nothing at all.
const endpoint: string = import.meta.env.VITE_ANALYTICS_ENDPOINT || '/api/events';

const TAB_KEY = 'awc.tab';

/**
 * Honours Global Privacy Control and Do Not Track. Both are widely ignored;
 * this app does not ignore them.
 */
function visitorHasOptedOut(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  if (nav.globalPrivacyControl === true) return true;
  if (nav.doNotTrack === '1' || nav.msDoNotTrack === '1') return true;
  return false;
}

/**
 * The per-tab identifier, created on first use.
 *
 * Returns undefined rather than inventing a fallback when storage is
 * unavailable — private browsing modes can throw on access, and an id that did
 * not persist would make one visitor look like twenty. The event is still sent
 * without it; it simply does not join a funnel.
 */
function tabId(): string | undefined {
  try {
    const existing = sessionStorage.getItem(TAB_KEY);
    if (existing) return existing;

    const created = crypto.randomUUID();
    sessionStorage.setItem(TAB_KEY, created);
    return created;
  } catch {
    return undefined;
  }
}

export function track(event: AnalyticsEvent): void {
  if (provider === 'none') return;
  if (visitorHasOptedOut()) return;

  // No client timestamp. The server stamps arrival time, which is the one it
  // can actually trust, and sending a second one would only invite the question
  // of which is right.
  const payload = { ...event, tabId: tabId() };

  if (provider === 'console') {
    // eslint-disable-next-line no-console
    console.info('[analytics]', payload);
    return;
  }

  if (provider === 'beacon' && endpoint) {
    try {
      const body = JSON.stringify(payload);
      if (typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
      } else {
        void fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => undefined);
      }
    } catch {
      // Analytics must never break the experience. Swallow and move on.
    }
  }
}
